import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { priorities } from "@/data/content";

export default function Priorities() {
  const [selected, setSelected] = useState(null);

  return (
    <section id="prioridades" data-testid="priorities-section" className="bg-slate-50 py-20 sm:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <SectionHeading
          chapter="02"
          kicker="Prioridades de campanha"
          title="12 bandeiras, um só compromisso: as pessoas"
          description="Cada prioridade nasce da escuta das comunidades do Extremo Sul. Toque em uma bandeira para saber mais."
        />

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {priorities.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: (i % 4) * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`priority-card-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="group flex flex-col rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-2 hover:border-brand-royal/40 hover:shadow-xl"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-royal to-brand-dark text-white shadow-lg transition-transform duration-200 group-hover:scale-110">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="font-display text-lg font-bold text-slate-900">{p.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{p.desc}</p>
                <button
                  type="button"
                  data-testid={`priority-saiba-mais-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setSelected(p)}
                  className="mt-4 self-start text-sm font-bold text-brand-orange transition-colors hover:text-brand-dark"
                >
                  Saiba mais →
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="priority-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-brand-navy/70 p-4 backdrop-blur-sm"
            onClick={() => setSelected(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label={`Prioridade: ${selected.title}`}
              className="relative w-full max-w-lg rounded-3xl bg-white p-8 shadow-2xl"
            >
              <button
                type="button"
                data-testid="priority-modal-close"
                aria-label="Fechar"
                onClick={() => setSelected(null)}
                className="absolute right-4 top-4 rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-royal to-brand-dark text-white">
                <selected.icon className="h-7 w-7" />
              </div>
              <h3 className="font-display text-2xl font-extrabold text-slate-900">{selected.title}</h3>
              <p className="mt-4 text-base leading-relaxed text-slate-600">{selected.detail}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
